/**
 * Authenticated workout (training-center class occurrence) detail
 * (`/treinos/:id`). Mirrors the mobile app: shows the class, its teachers and
 * participants, and lets the user confirm participation or cancel with a
 * consequence-aware preview.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { Body, Button, ButtonStyle, ButtonVariant, Caption, H4, Modal } from '@meiuca/ui-components'
import { ArrowLeft, Calendar, CheckCircle2, Circle, Clock, Loader2, MapPin, Users } from 'lucide-react'
import { NotFoundError } from '@meiuca/service'
import { formatDateForUI, parseAPIDate } from '@meiuca/web-foundation'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { useAuth } from '@/contexts/AuthContext'
import { toastManager } from '@/lib/toastManager'
import { formatLevel, formatTimeRange } from '@/lib/trainingCenterLabels'
import {
  workoutService,
  type WorkoutCancelKind,
  type WorkoutCancelPreview,
  type WorkoutDetail,
  type WorkoutParticipant,
  type WorkoutScopeParticipant
} from '@/services/workoutService'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error' }
  | { kind: 'ok'; workout: WorkoutDetail }

const CANCEL_MESSAGES: Record<string, string> = {
  contractWithMakeup: 'Ao cancelar, você ganha um crédito de reposição para usar em outra aula.',
  contractWithoutMakeup: 'Ao cancelar, você não terá reposição desta aula.',
  makeupCreditRelease: 'Ao cancelar, o crédito de reposição usado nesta aula será liberado de volta.',
  makeupCreditKept: 'Ao cancelar, o crédito de reposição usado nesta aula será mantido.',
  extraChargeCancellation: 'Ao cancelar, a cobrança avulsa desta aula será cancelada.',
  extraChargeOpenKept: 'A cobrança avulsa em aberto será mantida mesmo após o cancelamento.',
  extraChargeKeptWithCompensation: 'A cobrança será mantida, com compensação aplicada.',
  extraChargePaidKept: 'A cobrança já paga será mantida (fora da janela de cancelamento).'
}

const BLOCK_REASONS: Record<string, string> = {
  classOccurrenceAlreadyStarted: 'A aula já começou e não pode mais ser cancelada.',
  participantAlreadyCancelled: 'Sua participação nesta aula já está cancelada.'
}

export function WorkoutDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [confirming, setConfirming] = useState(false)

  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelPreview, setCancelPreview] = useState<WorkoutCancelPreview | null>(null)
  const [canceling, setCanceling] = useState(false)

  // Seleção de pessoas — só quando a conta age por mais de uma participação
  // na ocorrência (responsável com dependentes).
  const [confirmPeopleOpen, setConfirmPeopleOpen] = useState(false)
  const [cancelPeopleOpen, setCancelPeopleOpen] = useState(false)

  useEffect(() => {
    document.title = 'Treino · Meiuca'
    analyticsService.trackScreenView(SCREEN_NAMES.WORKOUT_DETAIL)
  }, [])

  const loadDetail = useCallback(async () => {
    if (!id) {
      setState({ kind: 'not-found' })
      return
    }
    try {
      const workout = await workoutService.getWorkoutDetail(id)
      setState({ kind: 'ok', workout })
    } catch (err) {
      setState({ kind: err instanceof NotFoundError ? 'not-found' : 'error' })
    }
  }, [id])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    loadDetail()
  }, [isAuthenticated, loadDetail, navigate])

  const handleConfirm = async () => {
    if (!id) return
    setConfirming(true)
    try {
      await workoutService.confirmParticipation(id)
      toastManager.success('Presença confirmada!')
      await loadDetail()
    } catch {
      toastManager.error('Não conseguimos confirmar sua presença. Tente novamente.')
    } finally {
      setConfirming(false)
    }
  }

  const openCancel = async () => {
    if (!id) return
    setCancelOpen(true)
    setCancelLoading(true)
    setCancelPreview(null)
    try {
      const preview = await workoutService.getWorkoutCancelPreview(id)
      setCancelPreview(preview)
    } catch {
      toastManager.error('Não conseguimos carregar as condições de cancelamento.')
      setCancelOpen(false)
    } finally {
      setCancelLoading(false)
    }
  }

  const confirmCancel = async () => {
    if (!id || !cancelPreview) return
    setCanceling(true)
    try {
      await workoutService.cancelWorkout(id, cancelPreview.kind)
      setCancelOpen(false)
      toastManager.success('Treino cancelado.')
      await loadDetail()
    } catch {
      toastManager.error('Não conseguimos cancelar o treino. Tente novamente.')
    } finally {
      setCanceling(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate('/treinos')}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Meus treinos
        </button>

        {state.kind === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando treino...</Body>
          </div>
        )}

        {state.kind === 'not-found' && (
          <EmptyState
            title="Treino não encontrado"
            message="O link pode estar incorreto ou o treino não está mais disponível."
          />
        )}

        {state.kind === 'error' && (
          <EmptyState
            title="Algo deu errado"
            message="Não conseguimos carregar este treino agora."
            onRetry={loadDetail}
          />
        )}

        {state.kind === 'ok' && (
          <WorkoutView
            workout={state.workout}
            confirming={confirming}
            onConfirm={handleConfirm}
            onCancel={openCancel}
            onConfirmPeople={() => setConfirmPeopleOpen(true)}
            onCancelPeople={() => setCancelPeopleOpen(true)}
          />
        )}
      </div>

      <CancelModal
        open={cancelOpen}
        loading={cancelLoading}
        preview={cancelPreview}
        canceling={canceling}
        onClose={() => setCancelOpen(false)}
        onConfirm={confirmCancel}
      />

      {state.kind === 'ok' && id && (
        <>
          <ConfirmPeopleModal
            open={confirmPeopleOpen}
            workoutId={id}
            people={confirmablePeople(state.workout)}
            onClose={() => setConfirmPeopleOpen(false)}
            onCompleted={() => {
              setConfirmPeopleOpen(false)
              loadDetail()
            }}
          />
          <CancelPeopleModal
            open={cancelPeopleOpen}
            workoutId={id}
            people={cancellablePeople(state.workout)}
            onClose={() => setCancelPeopleOpen(false)}
            onCompleted={() => {
              setCancelPeopleOpen(false)
              loadDetail()
            }}
          />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Escopo com mais de uma pessoa (responsável com dependentes)
// ---------------------------------------------------------------------------

function scopePersons(workout: WorkoutDetail): WorkoutScopeParticipant[] {
  return workout.scopeParticipants ?? []
}

function hasMultipleScopePersons(workout: WorkoutDetail): boolean {
  return scopePersons(workout).length > 1
}

function confirmablePeople(workout: WorkoutDetail): WorkoutScopeParticipant[] {
  return scopePersons(workout).filter((p) => p.status === 'cancelled')
}

function cancellablePeople(workout: WorkoutDetail): WorkoutScopeParticipant[] {
  return scopePersons(workout).filter((p) => p.status === 'confirmed')
}

function scopePersonName(person: WorkoutScopeParticipant): string {
  return person.isDependent ? person.studentCustomer.name : 'Você'
}

function EmptyState({
  title,
  message,
  onRetry
}: {
  title: string
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm border border-border-light">
      <H4 className="mb-2 text-text-primary">{title}</H4>
      <Body className="text-text-secondary">{message}</Body>
      {onRetry && (
        <div className="mt-4 flex justify-center">
          <Button variant={ButtonVariant.PRIMARY} onClick={onRetry}>
            Tentar novamente
          </Button>
        </div>
      )}
    </div>
  )
}

function WorkoutView({
  workout,
  confirming,
  onConfirm,
  onCancel,
  onConfirmPeople,
  onCancelPeople
}: {
  workout: WorkoutDetail
  confirming: boolean
  onConfirm: () => void
  onCancel: () => void
  onConfirmPeople: () => void
  onCancelPeople: () => void
}) {
  const level = formatLevel(workout.level ?? workout.trainingClass?.level)
  const isScheduled = workout.occurrenceStatus === 'scheduled'
  const isConfirmed = workout.myParticipantStatus === 'confirmed'
  // Com mais de uma pessoa no escopo, as ações são por status do conjunto —
  // cada botão abre a seleção de pessoas e o backend valida tudo-ou-nada.
  const multi = hasMultipleScopePersons(workout)
  const canCancel = isScheduled && (multi ? cancellablePeople(workout).length > 0 : isConfirmed)
  const canConfirm = isScheduled && (multi ? confirmablePeople(workout).length > 0 : !isConfirmed)

  const mapsLink =
    workout.location.latitude && workout.location.longitude
      ? `https://www.google.com/maps/search/?api=1&query=${workout.location.latitude},${workout.location.longitude}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workout.location.address)}`

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light space-y-3">
        <div className="flex items-start justify-between gap-3">
          <H4 className="text-text-primary">{workout.trainingCenter.name}</H4>
          <div className="flex flex-col items-end gap-1">
            {!isScheduled && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-error/15 text-feedback-error">
                Cancelado
              </span>
            )}
            {level && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-brand-primary/10 text-brand-primary">
                {level}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 text-text-secondary">
          <Calendar className="w-4 h-4" />
          <Body className="text-sm">{formatDateForUI(parseAPIDate(workout.date), 'long')}</Body>
        </div>
        <div className="flex items-center gap-2 text-text-secondary">
          <Clock className="w-4 h-4" />
          <Body className="text-sm">{formatTimeRange(workout.startTimeMin, workout.endTimeMin)}</Body>
        </div>
        <Caption className="text-text-secondary flex items-start gap-1">
          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {workout.location.name} — {workout.location.address}
          </span>
        </Caption>
        <a
          href={mapsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-brand-primary hover:underline"
        >
          <MapPin className="w-4 h-4" />
          Ver no mapa
        </a>
      </div>

      {/* Alunos — só quando a conta age por mais de uma participação
          (ex.: dois irmãos na mesma turma). Ações ficam nos botões globais. */}
      {multi && (
        <Section title="Alunos">
          <div className="space-y-2">
            {scopePersons(workout).map((person) => (
              <div key={person.participantId} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-background-secondary flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-text-tertiary" />
                  </div>
                  <Body className="text-sm text-text-primary truncate">{scopePersonName(person)}</Body>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    person.status === 'confirmed'
                      ? 'bg-feedback-success/20 text-feedback-success'
                      : 'bg-feedback-error/15 text-feedback-error'
                  }`}
                >
                  {person.status === 'confirmed' ? 'Confirmado' : 'Cancelado'}
                </span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Teachers */}
      {workout.teachers.length > 0 && (
        <Section title="Professores">
          <div className="flex flex-wrap gap-2">
            {workout.teachers.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center px-3 py-1.5 rounded-full bg-background-secondary text-text-primary text-sm"
              >
                {t.name}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Participants */}
      <Section title={`Participantes (${workout.participants.confirmed.length})`}>
        {workout.participants.confirmed.length === 0 ? (
          <Caption className="text-text-secondary">Nenhum participante confirmado ainda.</Caption>
        ) : (
          <div className="space-y-2">
            {workout.participants.confirmed.map((p) => (
              <ParticipantRow key={p.id} participant={p} />
            ))}
          </div>
        )}
      </Section>

      {/* Actions */}
      {(canConfirm || canCancel) && (
        <div className="flex flex-col gap-2">
          {canConfirm && (
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={multi ? onConfirmPeople : onConfirm}
              disabled={confirming}
              className="w-full justify-center"
            >
              {confirming ? 'Confirmando...' : 'Confirmar presença'}
            </Button>
          )}
          {canCancel && (
            <Button
              variant={ButtonVariant.NEGATIVE}
              onClick={multi ? onCancelPeople : onCancel}
              className="w-full justify-center"
            >
              Cancelar treino
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light">
      <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">
        {title}
      </h2>
      {children}
    </div>
  )
}

function ParticipantRow({ participant }: { participant: WorkoutParticipant }) {
  const level = formatLevel(participant.customer.level)
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-background-secondary flex items-center justify-center flex-shrink-0">
        <Users className="w-4 h-4 text-text-tertiary" />
      </div>
      <div className="min-w-0">
        <Body className="text-sm text-text-primary truncate">{participant.customer.name}</Body>
        {level && <Caption className="text-text-secondary">{level}</Caption>}
      </div>
    </div>
  )
}

function CancelModal({
  open,
  loading,
  preview,
  canceling,
  onClose,
  onConfirm
}: {
  open: boolean
  loading: boolean
  preview: WorkoutCancelPreview | null
  canceling: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const blocked = preview?.kind === 'blocked'
  const message = (() => {
    if (!preview) return ''
    if (preview.kind === 'blocked') {
      return preview.blockReason
        ? BLOCK_REASONS[preview.blockReason] ?? 'Não é possível cancelar este treino.'
        : 'Não é possível cancelar este treino.'
    }
    return CANCEL_MESSAGES[preview.kind] ?? 'Deseja realmente cancelar este treino?'
  })()

  return (
    <Modal open={open} onClose={onClose} title="Cancelar treino" size="sm">
      {loading ? (
        <div className="flex flex-col items-center py-6">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <Caption className="text-text-secondary mt-3">Carregando condições...</Caption>
        </div>
      ) : (
        <div className="space-y-5">
          <Body className="text-text-secondary">{message}</Body>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant={ButtonVariant.SECONDARY}
              buttonStyle={ButtonStyle.CLEAR}
              onClick={onClose}
              className="w-full justify-center"
            >
              Voltar
            </Button>
            {!blocked && (
              <Button
                variant={ButtonVariant.NEGATIVE}
                onClick={onConfirm}
                disabled={canceling}
                className="w-full justify-center"
              >
                {canceling ? 'Cancelando...' : 'Cancelar treino'}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Seleção de pessoas (responsável com dependentes) — operações tudo-ou-nada
// ---------------------------------------------------------------------------

function PersonSelectionRow({
  person,
  selected,
  onToggle
}: {
  person: WorkoutScopeParticipant
  selected: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-3 rounded-lg border border-border-light bg-background-secondary/40 px-3 py-2 text-left"
    >
      {selected ? (
        <CheckCircle2 className="w-5 h-5 text-brand-primary flex-shrink-0" />
      ) : (
        <Circle className="w-5 h-5 text-text-tertiary flex-shrink-0" />
      )}
      <Body className="text-sm text-text-primary flex-1 truncate">{scopePersonName(person)}</Body>
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${
          person.status === 'confirmed'
            ? 'bg-feedback-success/20 text-feedback-success'
            : 'bg-feedback-error/15 text-feedback-error'
        }`}
      >
        {person.status === 'confirmed' ? 'Confirmado' : 'Cancelado'}
      </span>
    </button>
  )
}

/**
 * Confirmação de presença com seleção de pessoas: pré-selecionadas, UMA
 * chamada tudo-ou-nada — ou todos entram, ou ninguém entra (ex.: sem vaga
 * para o conjunto).
 */
function ConfirmPeopleModal({
  open,
  workoutId,
  people,
  onClose,
  onCompleted
}: {
  open: boolean
  workoutId: string
  people: WorkoutScopeParticipant[]
  onClose: () => void
  onCompleted: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) setSelected(new Set(people.map((p) => p.participantId)))
  }, [open, people])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const submit = async () => {
    const participantIds = people.map((p) => p.participantId).filter((id) => selected.has(id))
    if (participantIds.length === 0 || submitting) return
    setSubmitting(true)
    try {
      await workoutService.confirmParticipants(workoutId, participantIds)
      analyticsService.track(ANALYTICS_EVENTS.WORKOUT_PRESENCE_CONFIRMED, {
        participant_count: participantIds.length,
        includes_dependent: people.some((p) => selected.has(p.participantId) && p.isDependent)
      })
      toastManager.success('Presença confirmada!')
      onCompleted()
    } catch (err) {
      const code = (err as { code?: string })?.code
      toastManager.error(
        code === 'class_occurrence_is_full'
          ? 'Não há vagas para todos os selecionados. Ninguém foi confirmado.'
          : 'Não conseguimos confirmar a presença. Tente novamente.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Confirmar presença de quem?" size="sm">
      <div className="space-y-5">
        <div className="space-y-2">
          {people.map((person) => (
            <PersonSelectionRow
              key={person.participantId}
              person={person}
              selected={selected.has(person.participantId)}
              onToggle={() => toggle(person.participantId)}
            />
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant={ButtonVariant.SECONDARY}
            buttonStyle={ButtonStyle.CLEAR}
            onClick={onClose}
            className="w-full justify-center"
          >
            Voltar
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={submit}
            disabled={submitting || selected.size === 0}
            className="w-full justify-center"
          >
            {submitting
              ? 'Confirmando...'
              : selected.size > 1
                ? `Confirmar (${selected.size})`
                : 'Confirmar presença'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

type CancelPeopleStep =
  | { kind: 'selection' }
  | { kind: 'previews-loading' }
  | { kind: 'previews-error' }
  | { kind: 'previews'; items: Array<{ person: WorkoutScopeParticipant; preview: WorkoutCancelPreview }> }

/**
 * Cancelamento com seleção de pessoas: seleção → prévia POR PESSOA (as
 * consequências diferem — um pode ganhar reposição, o outro não) → UMA chamada
 * tudo-ou-nada com o `expectedKind` de cada um. Qualquer divergência desfaz a
 * operação inteira e as prévias recarregam.
 */
function CancelPeopleModal({
  open,
  workoutId,
  people,
  onClose,
  onCompleted
}: {
  open: boolean
  workoutId: string
  people: WorkoutScopeParticipant[]
  onClose: () => void
  onCompleted: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [step, setStep] = useState<CancelPeopleStep>({ kind: 'selection' })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setSelected(new Set(people.map((p) => p.participantId)))
      // Uma pessoa só: sem etapa de seleção, direto pra prévia.
      if (people.length === 1) {
        loadPreviews(new Set(people.map((p) => p.participantId)))
      } else {
        setStep({ kind: 'selection' })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, people])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const loadPreviews = async (ids: Set<string>) => {
    const targets = people.filter((p) => ids.has(p.participantId))
    if (targets.length === 0) return
    setStep({ kind: 'previews-loading' })
    try {
      // Prévia por pessoa, buscadas em paralelo.
      const previews = await Promise.all(
        targets.map((person) =>
          workoutService
            .getParticipantCancelPreview(workoutId, person.participantId)
            .then((preview) => ({ person, preview }))
        )
      )
      setStep({ kind: 'previews', items: previews })
    } catch {
      setStep({ kind: 'previews-error' })
    }
  }

  const submit = async () => {
    if (step.kind !== 'previews' || submitting) return
    // Bloqueados ficam de fora da chamada.
    const targets = step.items
      .filter(({ preview }) => preview.kind !== 'blocked')
      .map(({ person, preview }) => ({
        participantId: person.participantId,
        expectedKind: preview.kind as WorkoutCancelKind
      }))
    if (targets.length === 0) return
    setSubmitting(true)
    try {
      await workoutService.cancelWorkoutParticipants(workoutId, targets)
      analyticsService.track(ANALYTICS_EVENTS.WORKOUT_PRESENCE_CANCELLED, {
        participant_count: targets.length,
        includes_dependent: step.items.some(
          ({ person, preview }) => preview.kind !== 'blocked' && person.isDependent
        )
      })
      toastManager.success('Treino cancelado.')
      onCompleted()
    } catch (err) {
      const code = (err as { code?: string })?.code
      toastManager.error(
        code === 'workout_cancellation_kind_mismatch'
          ? 'O resultado de um dos cancelamentos mudou. Ninguém foi cancelado — confira e confirme de novo.'
          : 'Não conseguimos cancelar o treino. Ninguém foi cancelado.'
      )
      // Tudo-ou-nada: nada foi cancelado; recarrega as prévias.
      await loadPreviews(selected)
    } finally {
      setSubmitting(false)
    }
  }

  const previewMessage = (preview: WorkoutCancelPreview): string => {
    if (preview.kind === 'blocked') {
      return preview.blockReason
        ? BLOCK_REASONS[preview.blockReason] ?? 'Esta participação não pode ser cancelada agora.'
        : 'Esta participação não pode ser cancelada agora.'
    }
    return CANCEL_MESSAGES[preview.kind] ?? 'O cancelamento será registrado.'
  }

  const hasCancellable = step.kind === 'previews' && step.items.some(({ preview }) => preview.kind !== 'blocked')
  const cancellableCount = step.kind === 'previews' ? step.items.filter(({ preview }) => preview.kind !== 'blocked').length : 0

  return (
    <Modal open={open} onClose={onClose} title="Cancelar treino" size="sm">
      {step.kind === 'selection' && (
        <div className="space-y-5">
          <Body className="text-text-secondary">Cancelar presença de quem?</Body>
          <div className="space-y-2">
            {people.map((person) => (
              <PersonSelectionRow
                key={person.participantId}
                person={person}
                selected={selected.has(person.participantId)}
                onToggle={() => toggle(person.participantId)}
              />
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant={ButtonVariant.SECONDARY}
              buttonStyle={ButtonStyle.CLEAR}
              onClick={onClose}
              className="w-full justify-center"
            >
              Voltar
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={() => loadPreviews(selected)}
              disabled={selected.size === 0}
              className="w-full justify-center"
            >
              Continuar
            </Button>
          </div>
        </div>
      )}

      {step.kind === 'previews-loading' && (
        <div className="flex flex-col items-center py-6">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <Caption className="text-text-secondary mt-3">Carregando condições...</Caption>
        </div>
      )}

      {step.kind === 'previews-error' && (
        <div className="space-y-5">
          <Body className="text-text-secondary">Não conseguimos carregar as condições de cancelamento.</Body>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant={ButtonVariant.SECONDARY}
              buttonStyle={ButtonStyle.CLEAR}
              onClick={onClose}
              className="w-full justify-center"
            >
              Fechar
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={() => loadPreviews(selected)}
              className="w-full justify-center"
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      )}

      {step.kind === 'previews' && (
        <div className="space-y-5">
          {/* Consequência por pessoa, lado a lado — nada de fundir prévias. */}
          <div className="space-y-3">
            {step.items.map(({ person, preview }) => (
              <div key={person.participantId} className="rounded-lg border border-border-light p-3">
                <Body className="text-sm font-semibold text-text-primary mb-1">
                  {scopePersonName(person)}
                </Body>
                <Caption className="text-text-secondary">{previewMessage(preview)}</Caption>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {people.length > 1 && (
              <Button
                variant={ButtonVariant.SECONDARY}
                buttonStyle={ButtonStyle.CLEAR}
                onClick={() => setStep({ kind: 'selection' })}
                className="w-full justify-center"
              >
                Mudar seleção
              </Button>
            )}
            <Button
              variant={ButtonVariant.SECONDARY}
              buttonStyle={ButtonStyle.CLEAR}
              onClick={onClose}
              className="w-full justify-center"
            >
              Voltar
            </Button>
            {hasCancellable && (
              <Button
                variant={ButtonVariant.NEGATIVE}
                onClick={submit}
                disabled={submitting}
                className="w-full justify-center"
              >
                {submitting
                  ? 'Cancelando...'
                  : cancellableCount > 1
                    ? `Cancelar (${cancellableCount})`
                    : 'Cancelar treino'}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
